/**
 * Leads Page - Lead tracking and analytics
 *
 * Shows:
 * - Summary cards (Total Leads, Applicants, Opt-ins) with animated numbers
 * - Funnel Breakdown (Applicant vs Opt-in pie chart)
 * - Lead Source Breakdown pie chart
 * - Country breakdown pie chart
 * - Total Leads over time trend chart
 * - Filterable and sortable leads table with server-side pagination
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, useSpring, useTransform, useInView, AnimatePresence } from "framer-motion";
import { ResponsivePie } from "@nivo/pie";
import {
  Area,
  Bar,
  BarChart,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { format } from "date-fns";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { FilterBar, StatCardSkeleton } from "../components";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { useActiveTheme } from "../context/ThemeContext";
import { branding } from "../config/branding";

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Normalize filter options that may come as either string[] or { value, label }[] objects
 * to a consistent { value: string; label: string }[] format.
 */
function normalizeToFilterOptions(data: unknown): Array<{ value: string; label: string }> {
  if (!Array.isArray(data)) return [];
  return data.map((item: unknown) => {
    if (typeof item === "string") return { value: item, label: item };
    if (item && typeof item === "object" && "value" in item) {
      const obj = item as { value: string; label?: string };
      return { value: obj.value, label: obj.label || obj.value };
    }
    return { value: String(item), label: String(item) };
  });
}

// ============================================================================
// TYPES
// ============================================================================

interface FilterOption {
  value: string;
  label: string;
}

interface Lead {
  _id: string;
  type: "applicant" | "optin";
  name: string;
  email: string;
  phone: string;
  country: string;
  countryCode: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  createdAt: string;
}

interface ChartDataItem {
  name: string;
  filterValue?: string;  // Real value for filtering (falls back to name)
  value: number;
}

interface DailyTrend {
  _id: string;
  applicants: number;
  optins: number;
  total: number;
}

interface Totals {
  total: number;
  applicants: number;
  optins: number;
}

interface LeadsDataSummary {
  totals: Totals;
  previousTotals: Totals | null;
  countryBreakdown: ChartDataItem[];
  sourceBreakdown: ChartDataItem[];
  funnelBreakdown: ChartDataItem[];
  funnelSourceBreakdown: ChartDataItem[];
  applicantIncomeBreakdown: ChartDataItem[];
  availableLeadTypes: string[];
  dailyTrend: DailyTrend[];
  leads: Lead[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filters: {
    sources: FilterOption[];
    funnels: FilterOption[];
    contents: FilterOption[];
    campaigns: FilterOption[];
    countries: FilterOption[];
  };
}

// ============================================================================
// CONSTANTS & UTILITIES
// ============================================================================

// Chart colors resolved inside component via useActiveTheme()

const formatNumber = (value: number) => value.toLocaleString();

// Country name to code mapping (reverse of what's in the API)
const countryNameToCode: Record<string, string> = {
  "USA/Canada": "1",
  "Russia": "7",
  "Egypt": "20",
  "South Africa": "27",
  "Greece": "30",
  "Netherlands": "31",
  "Belgium": "32",
  "France": "33",
  "Spain": "34",
  "Hungary": "36",
  "Italy": "39",
  "Romania": "40",
  "Switzerland": "41",
  "Austria": "43",
  "UK": "44",
  "Denmark": "45",
  "Sweden": "46",
  "Norway": "47",
  "Poland": "48",
  "Germany": "49",
  "Peru": "51",
  "Mexico": "52",
  "Cuba": "53",
  "Argentina": "54",
  "Brazil": "55",
  "Chile": "56",
  "Colombia": "57",
  "Venezuela": "58",
  "Malaysia": "60",
  "Australia": "61",
  "Indonesia": "62",
  "Philippines": "63",
  "New Zealand": "64",
  "Singapore": "65",
  "Thailand": "66",
  "Japan": "81",
  "South Korea": "82",
  "Vietnam": "84",
  "China": "86",
  "Turkey": "90",
  "India": "91",
  "Pakistan": "92",
  "Afghanistan": "93",
  "Sri Lanka": "94",
  "Myanmar": "95",
  "Iran": "98",
  "Morocco": "212",
  "Algeria": "213",
  "Tunisia": "216",
  "Libya": "218",
  "Gambia": "220",
  "Senegal": "221",
  "Nigeria": "234",
  "Kenya": "254",
  "Tanzania": "255",
  "Uganda": "256",
  "Zambia": "260",
  "Zimbabwe": "263",
  "Gibraltar": "350",
  "Portugal": "351",
  "Luxembourg": "352",
  "Ireland": "353",
  "Iceland": "354",
  "Albania": "355",
  "Malta": "356",
  "Cyprus": "357",
  "Finland": "358",
  "Bulgaria": "359",
  "Lithuania": "370",
  "Latvia": "371",
  "Estonia": "372",
  "Moldova": "373",
  "Armenia": "374",
  "Belarus": "375",
  "Ukraine": "380",
  "Serbia": "381",
  "Croatia": "385",
  "Slovenia": "386",
  "Bosnia": "387",
  "North Macedonia": "389",
  "Czech Republic": "420",
  "Slovakia": "421",
  "Hong Kong": "852",
  "Macau": "853",
  "Cambodia": "855",
  "Laos": "856",
  "Bangladesh": "880",
  "Taiwan": "886",
  "Maldives": "960",
  "Lebanon": "961",
  "Jordan": "962",
  "Syria": "963",
  "Iraq": "964",
  "Kuwait": "965",
  "Saudi Arabia": "966",
  "Yemen": "967",
  "Oman": "968",
  "UAE": "971",
  "Israel": "972",
  "Bahrain": "973",
  "Qatar": "974",
  "Bhutan": "975",
  "Mongolia": "976",
  "Nepal": "977",
  "Tajikistan": "992",
  "Turkmenistan": "993",
  "Azerbaijan": "994",
  "Georgia": "995",
  "Kyrgyzstan": "996",
  "Uzbekistan": "998",
};

// Reverse mapping: code to name
const countryCodeToName: Record<string, string> = Object.fromEntries(
  Object.entries(countryNameToCode).map(([name, code]) => [code, name])
);

// Nivo theme for charts
const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

// Custom tooltip for pie charts
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9, y: 5 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    className="bg-card/95 backdrop-blur-md text-foreground border border-border/50 rounded-xl shadow-2xl px-4 py-3 text-sm"
  >
    <div className="flex items-center gap-2.5">
      <div
        className="w-3.5 h-3.5 rounded-full shadow-inner"
        style={{ backgroundColor: datum.color, boxShadow: `0 0 8px ${datum.color}40` }}
      />
      <span className="font-semibold">{datum.id}</span>
    </div>
    <div className="mt-1.5 text-muted-foreground font-medium">
      {datum.value.toLocaleString()} <span className="text-foreground/60">({datum.data.percentage.toFixed(1)}%)</span>
    </div>
  </motion.div>
);

// Truncate long labels for pie charts
const truncateLabel = (label: string, maxLength: number = 18): string => {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 2) + "…";
};

// Transform data for Nivo pie chart
const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const merged = data.map((item, index) => ({
    id: item.name,
    label: item.name,
    filterValue: item.filterValue || item.name,  // Use filterValue if available, else name
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return (a.label || "").localeCompare(b.label || "");
  });
  return merged;
};

// ============================================================================
// ANIMATED NUMBER COMPONENT
// ============================================================================

// Animated number - only animates on initial load, not filter changes
function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const hasAnimatedOnce = useRef(false);

  const spring = useSpring(0, {
    stiffness: 50,
    damping: 20,
    duration: 1.2,
  });

  const display = useTransform(spring, (current) => Math.round(current).toLocaleString());
  const [displayValue, setDisplayValue] = useState("0");

  // Only animate the first time the component becomes visible
  useEffect(() => {
    if (isInView && !hasAnimatedOnce.current) {
      hasAnimatedOnce.current = true;
      spring.set(value);
    }
  }, [isInView, value, spring]);

  useEffect(() => {
    const unsubscribe = display.on("change", (v) => setDisplayValue(v));
    return unsubscribe;
  }, [display]);

  // When value changes after initial animation, update instantly (no animation)
  useEffect(() => {
    if (hasAnimatedOnce.current) {
      spring.jump(value);
    }
  }, [value, spring]);

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
}

// ============================================================================
// ICONS
// ============================================================================

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const UserPlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

const InboxIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Custom tooltip for Recharts line chart
interface ChartTooltipPayloadItem {
  value: number;
  name: string;
  color: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
  label?: string;
}

const CustomChartTooltip = ({ active, payload, label }: ChartTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 5, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-2xl px-4 py-3 text-sm"
    >
      <div className="font-semibold text-foreground">{label}</div>
      <div className="text-muted-foreground mt-1">
        <span className="text-red-400 font-bold">{payload[0]?.value?.toLocaleString()}</span> leads
      </div>
    </motion.div>
  );
};

// ============================================================================
// METRIC CARD COMPONENT (Premium version with glow effects)
// ============================================================================

interface LeadMetricCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: "brand" | "emerald" | "blue";
  index: number;
  percentChange?: number | null;
}

// Helper to convert hex color to RGB values
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result?.[1] || !result[2] || !result[3]) return "0,0,0";
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

// Get RGB values from branding colors
const brandRgb = hexToRgb(branding.colors.primary);

const colorConfig = {
  brand: {
    text: "text-[var(--brand-primary)]",
    gradient: "from-[var(--brand-primary)]/20 via-[var(--brand-primary)]/10 to-transparent",
    border: "border-[var(--brand-primary)]/20",
    iconBg: "bg-[var(--brand-primary)]/10",
    stroke: branding.colors.primary,
    // RGB values for shadow (computed from branding)
    glowRgb: brandRgb,
  },
  emerald: {
    text: "text-emerald-400",
    gradient: "from-emerald-500/20 via-emerald-500/10 to-transparent",
    border: "border-emerald-500/20",
    iconBg: "bg-emerald-500/10",
    stroke: "#34D399",
    glowRgb: "52,211,153",
  },
  blue: {
    text: "text-sky-400",
    gradient: "from-sky-500/20 via-sky-500/10 to-transparent",
    border: "border-sky-500/20",
    iconBg: "bg-sky-500/10",
    stroke: "#38BDF8",
    glowRgb: "56,189,248",
  },
};

function LeadMetricCard({ label, value, icon, color, index, percentChange }: LeadMetricCardProps) {
  const theme = colorConfig[color];
  const [isHovered, setIsHovered] = useState(false);

  // Dynamic shadow using branding colors
  const glowShadow = isHovered
    ? `0 0 50px -5px rgba(${theme.glowRgb},0.5)`
    : `0 0 30px -5px rgba(${theme.glowRgb},0.3)`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.5,
        delay: 0.1 + index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{
        transition: { duration: 0.2 },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        relative overflow-hidden
        bg-linear-to-br ${theme.gradient}
        ${theme.border} border rounded-2xl p-4 md:p-6
        transition-shadow duration-500 ease-out
        backdrop-blur-sm
      `}
      style={{ boxShadow: glowShadow }}
    >
      {/* Subtle animated gradient overlay */}
      <motion.div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${theme.stroke}15, transparent 50%)`,
        }}
      />

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
          >
            <motion.div
              className={`p-2 rounded-xl ${theme.iconBg} ${theme.text}`}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {icon}
            </motion.div>
            <span className="text-xs md:text-sm font-semibold text-foreground/80 uppercase tracking-wider">
              {label}
            </span>
          </motion.div>
          <div className="flex items-baseline gap-2 md:gap-3">
            <AnimatedNumber
              value={value}
              className="text-2xl md:text-4xl font-bold text-foreground tracking-tight"
            />
            {percentChange != null && (
              <motion.span
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className={`text-sm font-semibold ${
                  percentChange > 0
                    ? "text-emerald-400"
                    : percentChange < 0
                    ? "text-red-400"
                    : "text-muted-foreground"
                }`}
              >
                {percentChange > 0 ? "+" : ""}
                {percentChange.toFixed(2)}%
              </motion.span>
            )}
          </div>
        </div>

        {/* Decorative element */}
        <motion.div
          className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full ${theme.iconBg} opacity-50 blur-2xl`}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>
    </motion.div>
  );
}

// ============================================================================
// SUMMARY CARDS COMPONENT
// ============================================================================

function LeadsSummaryCards({ totals, previousTotals, isLoading }: { totals: Totals | null; previousTotals: Totals | null; isLoading: boolean }) {
  // Only show skeletons on initial load (no data yet), not during filter changes
  if (isLoading && !totals) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <StatCardSkeleton key={i} index={i} />
        ))}
      </div>
    );
  }

  if (!totals) return null;

  // Calculate percentage change: ((current - previous) / previous) * 100
  const calcPercentChange = (current: number, previous: number): number | null => {
    if (!previousTotals) return null;
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <LeadMetricCard
        label="Total Leads"
        value={totals.total}
        icon={<UsersIcon />}
        color="brand"
        index={0}
        percentChange={calcPercentChange(totals.total, previousTotals?.total ?? 0)}
      />
      <LeadMetricCard
        label="Applicants"
        value={totals.applicants}
        icon={<UserPlusIcon />}
        color="emerald"
        index={1}
        percentChange={calcPercentChange(totals.applicants, previousTotals?.applicants ?? 0)}
      />
      <LeadMetricCard
        label="Opt-ins"
        value={totals.optins}
        icon={<InboxIcon />}
        color="blue"
        index={2}
        percentChange={calcPercentChange(totals.optins, previousTotals?.optins ?? 0)}
      />
    </div>
  );
}

// ============================================================================
// CHART CARD WRAPPER
// ============================================================================

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
  delay?: number;
  height?: string;
  activeFilters?: string[];
  onClearFilters?: () => void;
}

function ChartCard({ title, children, delay = 0.4, height = "h-[300px] md:h-[400px]", activeFilters, onClearFilters }: ChartCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{
        y: -2,
        boxShadow: "0 20px 60px -15px rgba(0, 0, 0, 0.15)",
        transition: { duration: 0.3 },
      }}
      className="relative bg-card rounded-2xl border border-border p-4 md:p-6 overflow-hidden transition-all duration-300 hover:border-border/60"
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-linear-to-br from-white/2 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
        <motion.h3
          className="text-base md:text-lg font-bold tracking-tight"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: delay + 0.1 }}
        >
          {title}
        </motion.h3>
        <AnimatePresence>
          {activeFilters && activeFilters.length > 0 && onClearFilters && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={onClearFilters}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <span>Clear: {activeFilters.length > 2 ? `${activeFilters.slice(0, 2).join(", ")}...` : activeFilters.join(", ")}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      <div className={`${height} cursor-pointer`}>
        {children}
      </div>
    </motion.div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Leads() {
  const activeTheme = useActiveTheme();
  const PIE_COLORS = activeTheme.colors.chart;

  // Responsive pie chart margins - smaller on mobile to prevent tiny charts
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const pieMargin = isMobile
    ? { top: 20, right: 20, bottom: 20, left: 20 }
    : { top: 30, right: 120, bottom: 30, left: 120 };
  const { dateRange, formatForApi } = useDateRange();
  const {
    sources,
    countries,
    funnels,
    leadTypes,
    contents,
    offers,
    offer,
    setSources,
    setCountries,
    setFunnels,
    setLeadTypes,
    setContents,
    clearAllFilters,
  } = useFilters();

  // Local sync state for leads-only sync
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(Date.now());

  const triggerLeadsSync = useCallback(async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const response = await fetch("/api/analytics/sync/leads", { method: "POST" });
      const result = await response.json();
      if (result.success) {
        setLastSyncTime(Date.now());
      }
    } catch (err) {
      console.error("Leads sync failed:", err);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing]);

  const [isLoading, setIsLoading] = useState(true);
  const hasInitiallyLoaded = useRef(false); // Track if we've loaded data at least once
  const [summary, setSummary] = useState<LeadsDataSummary | null>(null);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Server-side search (with debouncing)
  const [searchInput, setSearchInput] = useState(""); // What user types
  const [searchQuery, setSearchQuery] = useState(""); // What we send to API (debounced)

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(1); // Reset to page 1 when search changes
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput, searchQuery]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams();

      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      // Multi-select filters (comma-separated)
      if (leadTypes.length > 0) params.set("type", leadTypes.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (countries.length > 0) params.set("country", countries.join(","));
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (contents.length > 0) params.set("content", contents.join(","));
      const selectedOffers = offers.length > 0 ? offers : (offer ? [offer] : []);
      if (selectedOffers.length > 0) params.set("offer", selectedOffers.join(","));

      // Add server-side search
      if (searchQuery.trim()) params.set("search", searchQuery.trim());

      // Add pagination params
      params.set("page", currentPage.toString());
      params.set("pageSize", pageSize.toString());

      const response = await fetch(`/api/leads-data/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        // Normalize filter options to consistent { value, label } format
        const normalizedData: LeadsDataSummary = {
          ...result.data,
          filters: {
            sources: normalizeToFilterOptions(result.data.filters?.sources),
            funnels: normalizeToFilterOptions(result.data.filters?.funnels),
            contents: normalizeToFilterOptions(result.data.filters?.contents),
            campaigns: normalizeToFilterOptions(result.data.filters?.campaigns),
            countries: normalizeToFilterOptions(result.data.filters?.countries),
          },
        };
        setSummary(normalizedData);
        setTotalCount(result.data.totalCount || 0);
        setTotalPages(result.data.totalPages || 1);
        hasInitiallyLoaded.current = true; // Mark that we've loaded data at least once
      }
    } catch (error) {
      console.error("Failed to fetch leads data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, formatForApi, leadTypes, sources, countries, funnels, contents, offer, offers, searchQuery, currentPage, pageSize, lastSyncTime]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, leadTypes, sources, countries, funnels, contents, offer, offers]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    if (!summary?.filters) return;
    const opts = summary.filters;

    // Skip if filterOptions haven't loaded yet
    if (!opts.sources?.length && !opts.funnels?.length && !opts.countries?.length) {
      return;
    }

    // Check source filter - now opts.sources is always FilterOption[]
    if (sources.length > 0 && opts.sources?.length > 0) {
      const validSources = sources.filter(s => opts.sources.some(opt => opt.value === s));
      if (validSources.length !== sources.length) {
        setSources(validSources);
      }
    }

    // Check funnel filter - now opts.funnels is always FilterOption[]
    if (funnels.length > 0 && opts.funnels?.length > 0) {
      const validFunnels = funnels.filter(f => opts.funnels.some(opt => opt.value === f));
      if (validFunnels.length !== funnels.length) {
        setFunnels(validFunnels);
      }
    }

    // Check content filter - now opts.contents is always FilterOption[]
    if (contents.length > 0 && opts.contents?.length > 0) {
      const validContents = contents.filter(c => opts.contents.some(opt => opt.value === c));
      if (validContents.length !== contents.length) {
        setContents(validContents);
      }
    }

    // Check country filter - now opts.countries is always FilterOption[]
    if (countries.length > 0 && opts.countries?.length > 0) {
      const validCountries = countries.filter(c => opts.countries.some(opt => opt.value === c));
      if (validCountries.length !== countries.length) {
        setCountries(validCountries);
      }
    }

    // Check lead type filter
    if (leadTypes.length > 0) {
      const availableLeadTypes = summary.availableLeadTypes || [];
      const validLeadTypes = leadTypes.filter((t) => availableLeadTypes.includes(t));
      if (validLeadTypes.length !== leadTypes.length) {
        setLeadTypes(validLeadTypes);
      }
    }
  }, [summary?.filters, summary?.availableLeadTypes, sources, funnels, contents, countries, leadTypes, setSources, setFunnels, setContents, setCountries, setLeadTypes]);

  // Handle clear all filters
  const handleClearAll = () => {
    setCurrentPage(1);
    setSearchInput("");
    setSearchQuery("");
    clearAllFilters();
  };

  // Chart click handlers - toggle items in multi-select arrays
  const handleFunnelClick = (name: string) => {
    // Map display name to filter value
    const value = name === "Applicant" ? "applicant" : name === "Opt-in" ? "optin" : name.toLowerCase();
    if (leadTypes.includes(value)) {
      setLeadTypes(leadTypes.filter((t) => t !== value));
    } else {
      setLeadTypes([...leadTypes, value]);
    }
  };

  const handleSourceClick = (filterValue: string) => {
    if (filterValue === "Other" || filterValue === "N/A") return;
    if (sources.includes(filterValue)) {
      setSources(sources.filter((s) => s !== filterValue));
    } else {
      setSources([...sources, filterValue]);
    }
  };

  const handleFunnelSourceClick = (filterValue: string) => {
    if (filterValue === "Other" || filterValue === "N/A") return;
    // filterValue is the real value, use it directly
    if (funnels.includes(filterValue)) {
      setFunnels(funnels.filter((f) => f !== filterValue));
    } else {
      setFunnels([...funnels, filterValue]);
    }
  };

  const handleCountryClick = (name: string) => {
    if (name === "Other") return;
    // Convert display name to country code for the filter
    const countryCode = countryNameToCode[name] || name;
    if (countries.includes(countryCode)) {
      setCountries(countries.filter((c) => c !== countryCode));
    } else {
      setCountries([...countries, countryCode]);
    }
  };

  // Table columns matching old dashboard
  const columns: ColumnDef<Lead>[] = useMemo(() => [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      className: "font-medium min-w-[120px]",
    },
    {
      id: "email",
      header: "Email",
      cell: (row) => (
        <span className="text-sm truncate max-w-[150px] block text-muted-foreground" title={row.email}>
          {row.email || "-"}
        </span>
      ),
    },
    {
      id: "phone",
      header: "Phone",
      cell: (row) => (
        <span className="text-sm font-mono truncate max-w-[120px] block text-muted-foreground">{row.phone || "-"}</span>
      ),
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => (
        <span className="text-sm px-2 py-1 rounded-lg bg-muted/50">{row.utm_source || "N/A"}</span>
      ),
    },
    {
      id: "funnel",
      header: "Funnel",
      cell: (row) => (
        <span className="text-sm truncate max-w-[120px] block" title={row.utm_medium}>
          {row.utm_medium || "N/A"}
        </span>
      ),
    },
    {
      id: "campaign",
      header: "Campaign",
      cell: (row) => (
        <span className="text-sm truncate max-w-[100px] block text-muted-foreground" title={row.utm_campaign}>
          {row.utm_campaign || "N/A"}
        </span>
      ),
    },
    {
      id: "content",
      header: "Content",
      cell: (row) => (
        <span className="text-sm truncate max-w-[100px] block text-muted-foreground" title={row.utm_content}>
          {row.utm_content || "N/A"}
        </span>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => {
        const isApplicant = row.type === "applicant";
        return (
          <motion.span
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`
              inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
              ${isApplicant
                ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                : "bg-sky-500/10 text-sky-400 border border-sky-500/20"
              }
            `}
          >
            <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isApplicant ? "bg-emerald-500" : "bg-sky-400"}`} />
            {isApplicant ? "Applicant" : "Opt-in"}
          </motion.span>
        );
      },
    },
    {
      id: "date",
      header: "Date",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.createdAt ? format(new Date(row.createdAt), "MMM d, yyyy") : "-"}
        </span>
      ),
      className: "whitespace-nowrap",
    },
  ], []);

  // Filter options are now already normalized to FilterOption[] format
  const filterOptions = summary?.filters || {
    sources: [] as FilterOption[],
    funnels: [] as FilterOption[],
    contents: [] as FilterOption[],
    campaigns: [] as FilterOption[],
    countries: [] as FilterOption[],
  };

  const leadTypeFilterOptions = useMemo(() => {
    const available = summary?.availableLeadTypes || ["applicant", "optin"];
    return [
      available.includes("applicant") ? { value: "applicant", label: "Applicant" } : null,
      available.includes("optin") ? { value: "optin", label: "Opt-in" } : null,
    ].filter((option): option is { value: string; label: string } => option !== null);
  }, [summary?.availableLeadTypes]);

  // Create funnel breakdown data (Applicant vs Opt-in)
  const funnelBreakdownData = useMemo(() => {
    if (!summary?.totals) return [];
    return [
      { name: "Applicant", value: summary.totals.applicants },
      { name: "Opt-in", value: summary.totals.optins },
    ].filter(item => item.value > 0);
  }, [summary?.totals]);

  // Create source breakdown data with "N/A" for missing sources
  const sourceBreakdownData = useMemo(() => {
    if (!summary?.sourceBreakdown) return [];
    return summary.sourceBreakdown.map(item => ({
      name: item.name || "N/A",
      value: item.value,
    }));
  }, [summary?.sourceBreakdown]);

  const funnelSourceBreakdownData = useMemo(() => {
    if (!summary?.funnelSourceBreakdown) return [];
    return summary.funnelSourceBreakdown.map((item) => ({
      name: item.name || "N/A",
      value: item.value,
    }));
  }, [summary?.funnelSourceBreakdown]);

  const applicantIncomeBreakdownData = useMemo(() => {
    if (!summary?.applicantIncomeBreakdown) return [];
    return summary.applicantIncomeBreakdown;
  }, [summary?.applicantIncomeBreakdown]);

  // Leads are now filtered server-side, just return them directly
  const filteredLeads = useMemo(() => {
    return summary?.leads || [];
  }, [summary?.leads]);

  // Determine if "All Time" is selected (year 2000 or earlier means all time)
  const isAllTime = dateRange.startDate.getFullYear() <= 2000;

  // Chart data for Recharts - aggregate by month for All Time, daily for custom range
  const chartData = useMemo(() => {
    if (!summary?.dailyTrend) return [];

    if (isAllTime) {
      // Aggregate by month for All Time view
      const monthlyData = new Map<string, number>();

      summary.dailyTrend.forEach((d) => {
        const date = new Date(d._id);
        const monthKey = format(date, "yyyy-MM");
        const current = monthlyData.get(monthKey) || 0;
        monthlyData.set(monthKey, current + d.total);
      });

      // Convert to array and sort by date
      return Array.from(monthlyData.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([monthKey, total]) => ({
          date: format(new Date(monthKey + "-01"), "MMM yyyy"),
          total,
        }));
    } else {
      // Show daily data for custom date ranges
      return summary.dailyTrend.map((d) => ({
        date: format(new Date(d._id), "MMM d"),
        total: d.total,
      }));
    }
  }, [summary?.dailyTrend, isAllTime]);

  // Only show full skeleton on initial load, not when filtering
  if (isLoading && !hasInitiallyLoaded.current) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="h-10 w-48 rounded-xl bg-muted animate-pulse mx-auto" />
        </motion.div>

        {/* Filter Bar Skeleton */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="h-16 rounded-xl bg-muted/30 animate-pulse"
        />

        {/* Summary Cards Skeleton */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-2xl border border-border p-6"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted animate-pulse" />
                  <div className="h-4 w-24 rounded bg-muted animate-pulse" />
                </div>
                <div className="h-10 w-32 rounded-lg bg-muted animate-pulse" />
              </div>
              <motion.div
                className="absolute inset-0"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Chart Skeletons */}
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-2xl border border-border p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[400px] rounded-xl bg-muted/30 animate-pulse flex items-center justify-center">
                <motion.div
                  className="w-32 h-32 rounded-full border-4 border-muted border-t-muted-foreground/30"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                />
              </div>
              <motion.div
                className="absolute inset-0"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                style={{
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  const totals = summary?.totals || { total: 0, applicants: 0, optins: 0 };

  return (
    <div className="space-y-8 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0"
      >
        <div className="hidden sm:block flex-1" /> {/* Spacer for centering */}
        <div className="text-center">
          <motion.h1
            className="text-2xl md:text-4xl font-bold tracking-tight"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <span className="bg-linear-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent">
              LEADS DATA
            </span>
          </motion.h1>
          <motion.p
            className="text-sm md:text-base text-muted-foreground mt-1 md:mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Track and analyze your lead generation performance
          </motion.p>
        </div>
        <div className="sm:flex-1 flex justify-end">
          <motion.button
            onClick={triggerLeadsSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg bg-card border border-border hover:bg-secondary/50 transition-colors disabled:opacity-50 text-sm md:text-base"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <svg
              className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="text-sm font-medium">
              {isSyncing ? "Syncing..." : "Sync Leads"}
            </span>
          </motion.button>
        </div>
      </motion.div>

      {/* Filter Bar */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <FilterBar
          filters={[
            {
              key: "source",
              label: "Select Source",
              options: filterOptions.sources,
              value: sources,
              onChange: (v) => setSources(v as string[]),
              multiple: true,
            },
            {
              key: "funnel",
              label: "Select Funnel",
              options: filterOptions.funnels,
              value: funnels,
              onChange: (v) => setFunnels(v as string[]),
              multiple: true,
            },
            {
              key: "content",
              label: "Select UTM Content",
              options: filterOptions.contents,
              value: contents,
              onChange: (v) => setContents(v as string[]),
              multiple: true,
            },
            {
              key: "type",
              label: "Lead Type",
              options: leadTypeFilterOptions,
              value: leadTypes,
              onChange: (v) => setLeadTypes(v as string[]),
              multiple: true,
            },
            {
              key: "country",
              label: "Select Country",
              options: filterOptions.countries,
              value: countries,
              onChange: (v) => setCountries(v as string[]),
              multiple: true,
            },
          ]}
          onClearAll={handleClearAll}
        />
      </motion.div>

      {/* Leads Summary Cards */}
      <LeadsSummaryCards totals={totals} previousTotals={summary?.previousTotals ?? null} isLoading={isLoading} />

      {/* Lead Type & Lead Source Breakdown */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Lead Type Breakdown (Applicant vs Opt-in) */}
        <ChartCard
          title="LEAD TYPE"
          delay={0.4}
          activeFilters={leadTypes.map(t => t === "applicant" ? "Applicant" : "Opt-in")}
          onClearFilters={() => setLeadTypes([])}
        >
          {funnelBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(funnelBreakdownData, [PIE_COLORS[4] || "#504df9", PIE_COLORS[0] || "#09bef5"])}
              margin={pieMargin}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={!isMobile}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleFunnelClick(String(node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <UsersIcon />
                </div>
                No lead type data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>

        {/* Lead Source Breakdown */}
        <ChartCard
          title="LEAD SOURCE BREAKDOWN"
          delay={0.5}
          activeFilters={sources}
          onClearFilters={() => setSources([])}
        >
          {sourceBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(sourceBreakdownData, PIE_COLORS)}
              margin={pieMargin}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={!isMobile}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => `${truncateLabel(String(d.label))} (${d.data.percentage.toFixed(1)}%)`}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={false}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleSourceClick(String(node.data.filterValue || node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <SearchIcon />
                </div>
                No source data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Funnel Source + Applicant Income */}
      <div className="grid gap-6 md:grid-cols-2">
        <ChartCard
          title="FUNNEL SOURCE BREAKDOWN"
          delay={0.55}
          activeFilters={funnels.map((value) => {
            const matching = filterOptions.funnels.find((opt) => opt.value === value);
            return matching ? matching.label : value;
          })}
          onClearFilters={() => setFunnels([])}
        >
          {funnelSourceBreakdownData.length > 0 ? (
            <ResponsivePie
              data={toNivoPieData(funnelSourceBreakdownData, PIE_COLORS)}
              margin={pieMargin}
              innerRadius={0.55}
              padAngle={2}
              cornerRadius={6}
              activeOuterRadiusOffset={10}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={0}
              enableArcLinkLabels={!isMobile}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => `${truncateLabel(String(d.label))} (${d.data.percentage.toFixed(1)}%)`}
              arcLinkLabelsStraightLength={12}
              arcLinkLabelsDiagonalLength={12}
              enableArcLabels={false}
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
              onClick={(node) => handleFunnelSourceClick(String(node.data.filterValue || node.label))}
              animate
              motionConfig="gentle"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <SearchIcon />
                </div>
                No funnel source data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="APPLICANT MONTHLY INCOME (ON PAPER)"
          delay={0.6}
        >
          {applicantIncomeBreakdownData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={applicantIncomeBreakdownData} margin={{ top: 20, right: 15, bottom: 20, left: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="currentColor"
                  strokeOpacity={0.1}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.6 }}
                />
                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.6 }}
                />
                <Tooltip
                  cursor={{ fill: "hsl(var(--muted))", fillOpacity: 0.2 }}
                  contentStyle={{
                    backgroundColor: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "10px",
                    color: "hsl(var(--popover-foreground))",
                    boxShadow: "0 10px 25px -15px hsl(var(--foreground) / 0.4)",
                  }}
                  itemStyle={{ color: "hsl(var(--popover-foreground))" }}
                  labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                  formatter={(value) => [`${Number(value || 0).toLocaleString()} applicants`, "Count"]}
                />
                <Bar
                  dataKey="value"
                  fill={PIE_COLORS[2] || "#22c55e"}
                  radius={[8, 8, 0, 0]}
                  maxBarSize={56}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13h3l3 8 4-16 3 8h5" />
                  </svg>
                </div>
                No applicant income data available for the selected filters.
              </motion.div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Country Breakdown Chart */}
      <ChartCard
        title="COUNTRY BREAKDOWN"
        delay={0.6}
        activeFilters={countries.map(c => countryCodeToName[c] || c)}
        onClearFilters={() => setCountries([])}
      >
        {(summary?.countryBreakdown || []).length > 0 ? (
          <ResponsivePie
            data={toNivoPieData(summary?.countryBreakdown || [], PIE_COLORS)}
            margin={pieMargin}
            innerRadius={0.55}
            padAngle={2}
            cornerRadius={6}
            activeOuterRadiusOffset={10}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={0}
            enableArcLinkLabels={!isMobile}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => `${truncateLabel(String(d.label))} (${d.data.percentage.toFixed(1)}%)`}
            arcLinkLabelsStraightLength={12}
            arcLinkLabelsDiagonalLength={12}
            enableArcLabels={false}
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
            onClick={(node) => handleCountryClick(String(node.label))}
            animate
            motionConfig="gentle"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              No country data available for the selected filters.
            </motion.div>
          </div>
        )}
      </ChartCard>

      {/* Total Leads Over Time Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        whileHover={{
          y: -2,
          boxShadow: "0 20px 60px -15px rgba(0, 0, 0, 0.15)",
          transition: { duration: 0.3 },
        }}
        className="bg-card rounded-2xl border border-border p-4 md:p-6 overflow-hidden transition-all duration-300 hover:border-border/60"
      >
        <motion.h3
          className="text-base md:text-lg font-bold mb-4 tracking-tight flex flex-wrap items-center gap-2"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
        >
          TOTAL LEADS OVER TIME
          <span className="text-xs font-normal text-muted-foreground px-2 py-0.5 rounded-full bg-muted">
            {isAllTime ? "Monthly" : "Daily"}
          </span>
        </motion.h3>
        <div className="h-[250px] md:h-[350px]">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 20, bottom: 20, left: 10 }}
              >
                <defs>
                  <linearGradient id="leadsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                    <stop offset="50%" stopColor="#ef4444" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="currentColor"
                  strokeOpacity={0.1}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
                  dy={10}
                  interval={Math.max(0, Math.floor(chartData.length / 8) - 1)}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
                  tickFormatter={(value) => value.toLocaleString()}
                  width={45}
                />
                <Tooltip content={<CustomChartTooltip />} cursor={{ stroke: '#ef4444', strokeWidth: 1, strokeDasharray: '4 4', strokeOpacity: 0.5 }} />
                <Area
                  type="monotone"
                  dataKey="total"
                  fill="url(#leadsGradient)"
                  stroke="none"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#ef4444"
                  strokeWidth={3}
                  dot={false}
                  filter="url(#glow)"
                  activeDot={{
                    fill: "#ef4444",
                    strokeWidth: 3,
                    stroke: "#ffffff",
                    r: 6,
                    filter: "url(#glow)"
                  }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                No trend data available for the selected filters.
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Leads Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
          <div>
            <motion.h3
              className="text-xl md:text-2xl font-bold tracking-tight"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
            >
              All Leads
            </motion.h3>
            <motion.p
              className="text-xs md:text-sm text-muted-foreground mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.95 }}
            >
              Browse and search through your lead database
            </motion.p>
          </div>

          {/* Search and Filter Controls */}
          <motion.div
            className="flex items-center gap-2 md:gap-3 w-full sm:w-auto"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9 }}
          >
            {/* Search Input */}
            <div className="relative group flex-1 sm:flex-none">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-foreground transition-colors">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-9 md:h-10 w-full sm:w-[280px] rounded-xl border border-border bg-background pl-9 md:pl-10 pr-4 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring placeholder:text-muted-foreground hover:border-border/80"
              />
              {/* Loading indicator when search is pending */}
              {searchInput !== searchQuery && (
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Type Filter Dropdown - triggers server-side filtering */}
            <select
              value={leadTypes.length === 0 ? "all" : leadTypes.length === 1 ? leadTypes[0] : "all"}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "all") {
                  setLeadTypes([]);
                } else {
                  setLeadTypes([value]);
                }
                setCurrentPage(1);
              }}
              className="h-9 md:h-10 rounded-xl border border-border bg-background px-3 md:px-4 text-xs md:text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring hover:border-border/80 cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="applicant">Applicants Only</option>
              <option value="optin">Opt-ins Only</option>
            </select>
          </motion.div>
        </div>

        <motion.div
          whileHover={{
            boxShadow: "0 20px 60px -15px rgba(0, 0, 0, 0.1)",
            transition: { duration: 0.3 },
          }}
          className="rounded-2xl overflow-hidden"
        >
          <DataTable
            data={filteredLeads}
            columns={columns}
            rowKey="_id"
            loading={isLoading}
            emptyMessage="No leads found for the selected filters."
          />
        </motion.div>

        {/* Server-side Pagination Controls */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 sm:mt-6 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          <p className="text-xs sm:text-sm text-muted-foreground">
            {totalCount > 0 ? (
              <>
                <span className="font-semibold text-foreground">{((currentPage - 1) * pageSize) + 1}</span> to{" "}
                <span className="font-semibold text-foreground">{Math.min(currentPage * pageSize, totalCount)}</span> of{" "}
                <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span> row(s)
                {searchQuery && <span className="hidden sm:inline text-muted-foreground/60"> (filtered)</span>}
              </>
            ) : (
              <>0 of 0 row(s){searchQuery && <span className="hidden sm:inline text-muted-foreground/60"> (no matches)</span>}</>
            )}
          </p>
          <div className="flex items-center gap-3 sm:gap-6">
            {/* Rows per page selector - hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-9 w-[70px] rounded-lg border border-border bg-background px-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring/50 hover:border-border/80 cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Page info */}
            <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
              <span className="font-semibold text-foreground">{currentPage}</span> / {" "}
              <span className="font-semibold text-foreground">{totalPages || 1}</span>
            </span>

            {/* Navigation buttons */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              {/* First page - hidden on mobile */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="First page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
              {/* Last page - hidden on mobile */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="hidden sm:flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Last page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
